<template>
    <ideo-form-group :label="$t('[[[Wybierz autora]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <ideo-autocomplete
                    v-model="authorId"
                    name="authorId"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać autora...]]]')"
                    :fetch="(id: number) => fetchAuthor(id)"
                    :search="(query: string, limit: number) => searchAuthor(query, limit)"
                    :refresh="true"
                >
                    <template #selected="{ option }">
                        <div class="d-flex flex-nowrap align-items-center">
                            <ideo-img :src="$filters.image(option.facePictureUrl, 'ico')" rounded="circle" :blank="!option.facePictureUrl" width="18" height="18" blank-color="#777" />
                            <b class="ms-2">{{ option.userName }}</b>
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="d-flex flex-nowrap align-items-center">
                            <ideo-img :src="$filters.image(option.facePictureUrl, 'ico')" rounded="circle" :blank="!option.facePictureUrl" width="36" height="36" blank-color="#777" />
                            <b class="ms-2">{{ option.userName }}</b>
                        </div>
                    </template>
                </ideo-autocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addAuthor()">
                    <i class="fas fa-plus"></i>
                </button>
            </div>
        </div>
    </ideo-form-group>
    <ideo-form-group :label="$t('[[[Cechy w grupie]]]')">
        <div class="list-group">
            <div v-for="item in authors" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <div class="col-md-11 d-flex align-items-center">
                    <span class="me-2">
                        <i class="fad fa-fw fa-browser text-primary"></i>
                    </span>
                    <span class="me-auto">
                        <span class="d-block" :title="$t('[[[Autor]]]')">
                            <ideo-img :src="$filters.image(item.facePictureUrl, 'ico')" rounded="circle" :blank="!item.facePictureUrl" width="18" height="18" blank-color="#777" />
                            <b class="ms-2">{{ item.userName }}</b>
                        </span>
                    </span>
                </div>
                <span class="d-flex align-items-center">
                    <ideo-button variant="danger" size="sm" pill :id="`btn-delete-related-${item.id}`" :title="$t('[[[Usuń autora]]]')">
                        <i class="fas fa-trash-alt"></i>
                    </ideo-button>
                    <confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.id}`" :value="item.id" @confirm="removeAuthor(item.id)" />
                </span>
            </div>
            <div class="list-group-item list-group-item-light text-center" v-if="authors.length == 0">
                {{ $t('[[[Brak autorów]]]') }}
            </div>
        </div>
    </ideo-form-group>
</template>

<script lang="ts" setup>
import { FormType } from '@/helpers/Form';
import AuthorsService, { ListItemModel } from '@/modules/cms/authors/services/AuthorsService';
import { useWebsitesStore } from '@/modules/cms/websites/store';
import { useAlerts } from '@/plugins/alerts';
import { useLogging } from '@/plugins/logging';
import { useMixins } from '@/plugins/mixins';
import { computedAsync } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';

defineOptions({
    name: 'widget-authors-settings'
});

const { $log } = useLogging();
const { $handleException } = useMixins();
const { $alert } = useAlerts();

const websiteStore = useWebsitesStore();

interface FormSettings
{
    settings: {
        authorsIds: number[],
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    authorsIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = {...defaults, ...form.value.settings};
});

const authorId = ref<number>(null);
const authors = computedAsync(async () =>
{
    try
    {
        return await AuthorsService.getListByIds(form.value.settings.authorsIds);
    }
    catch (ex)
    {
        return [];
    }
});

const fetchAuthor = async (id: number): Promise<ListItemModel> =>
{
    const response = await AuthorsService.fetchAutocompleteModel(id);

    return response || null;
};

const searchAuthor = async (query: string, limit: number): Promise<ListItemModel[]> =>
{
    return await AuthorsService.getAutocompleteList(
        query,
        limit,
        websiteStore.current.website,
        websiteStore.current.locale,
        form.value.settings.authorsIds);
};

const addAuthor = async (): Promise<void> =>
{
    if (authorId.value == null)
        return;

    try
    {
        form.value.settings.authorsIds.push(authorId.value);
        authorId.value = null;
    }
    catch (ex)
    {
        $handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
};

const removeAuthor = async (auhtorId: number): Promise<void> =>
{
    form.value.settings.authorsIds = form.value.settings.authorsIds.filter(x => x != auhtorId);
};
</script>
